import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/NewButton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Label } from '@/components/ui/label';
import { RotateCcw, Expand } from 'lucide-react';
import { Loading } from '@/components/shared';
import { toast } from 'react-hot-toast';
import { PromptResponse } from './types';
import { format } from 'date-fns';
import type { PromptLibraryApi } from '@/lib/promptLibraryApi';

interface PromptVersionHistoryProps {
  api: PromptLibraryApi;
  promptGroupId: string;
  onRestore: (version: PromptResponse) => void;
}

export const PromptVersionHistory: React.FC<PromptVersionHistoryProps> = ({
  api,
  promptGroupId,
  onRestore,
}) => {
  const [versions, setVersions] = useState<PromptResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewVersion, setPreviewVersion] = useState<PromptResponse | null>(null);

  const fetchVersions = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.listVersions(promptGroupId);
      setVersions(data);
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Failed to load version history');
    } finally {
      setIsLoading(false);
    }
  }, [api, promptGroupId]);

  useEffect(() => {
    void fetchVersions();
  }, [fetchVersions]);

  return (
    <div className="flex-1 min-w-0 border-l border-slate-200 pl-4 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <Label className="text-sm font-medium">History</Label>
      </div>
      <ScrollArea className="flex-1 min-h-0">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loading />
          </div>
        ) : versions.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">No history</p>
        ) : (
          <div className="space-y-2 pr-2">
            {versions.map((version, index) => (
              <div
                key={version.version}
                className="p-2.5 rounded-lg border border-slate-200 hover:border-slate-300 transition-colors"
                data-testid="version-entry"
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium text-slate-500">
                      v{version.version}
                    </span>
                    {index === 0 && (
                      <span className="text-[10px] font-medium bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                        Current
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {index > 0 && (
                      <Button
                        size="xs"
                        variant="ghost"
                        icon={RotateCcw}
                        text="Restore"
                        onClick={() => onRestore(version)}
                      />
                    )}
                    <Button
                      size="xs"
                      variant="ghost"
                      icon={Expand}
                      className="text-slate-400 hover:text-slate-700"
                      onClick={() => setPreviewVersion(version)}
                      title="View full content"
                    />
                  </div>
                </div>
                <p className="text-xs font-medium text-slate-900 truncate">{version.title}</p>
                <p className="text-xs text-slate-600 line-clamp-2 mt-0.5">{version.content}</p>
                <div className="text-[10px] text-slate-400 mt-1">
                  {format(new Date(version.created_at), 'd MMM yyyy, h:mm a')}
                  {version.created_by_name && ` · ${version.created_by_name}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </ScrollArea>

      {previewVersion && (
        <Dialog open={!!previewVersion} onOpenChange={() => setPreviewVersion(null)}>
          <DialogContent className="sm:max-w-[600px] max-h-[70vh] flex flex-col overflow-hidden">
            <DialogHeader className="shrink-0">
              <DialogTitle>
                {previewVersion.title}
                <span className="ml-2 text-sm font-normal text-slate-500">v{previewVersion.version}</span>
              </DialogTitle>
              <DialogDescription>
                {format(new Date(previewVersion.created_at), 'd MMM yyyy, h:mm a')}
                {previewVersion.created_by_name && ` · ${previewVersion.created_by_name}`}
              </DialogDescription>
            </DialogHeader>
            <ScrollArea className="flex-1 min-h-0 overflow-y-auto">
              <div className="text-sm text-slate-700 whitespace-pre-wrap pr-4">
                {previewVersion.content}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};
